import type {
  IDraftCategory,
  IDraftItem,
  IMenuDraft,
  IMenuImportTurn,
} from '@/interfaces/IMenuImport';
import {
  categoryItems,
  matchesItemFilter,
  orderedCategories,
} from './menu-draft';
import type { TItemFilter } from './menu-draft';
import { isUpdateDraft, liveItemState } from './live-update';
import {
  isCategoryRecord,
  isItemRecord,
  latestPendingProposals,
  rowDecorations,
} from './proposal-effects';
import type { TRowDecoration } from './proposal-effects';

/**
 * The rows of the workspace list (plan §4.5 «Category block», «Dish row»):
 * categories in menu order with their filtered dishes, plus the marks of the
 * pending proposals of the latest chat turn: dishes it would add («Nuevo»,
 * ghost rows that are not in the draft yet), change («Cambiado», the new
 * values with the old ones struck) or remove («Se quita»).
 */

/** Categories with more dishes than this start collapsed, unless something in them needs review. */
export const COLLAPSE_MIN_ITEMS = 25;

export interface IProposalAddition<T> {
  /** Unique across proposals (two proposals of a turn may reuse the same new id). */
  key: string;
  /** Index of the proposal among the latest turn's pending ones. */
  proposal: number;
  record: T;
}

export interface IProposalPreview {
  /** Dish id → mark. */
  decorations: Record<string, TRowDecoration>;
  /** The proposed record of every dish marked «Cambiado». */
  changed: Record<string, IDraftItem>;
  /** Dishes the proposals would add. */
  added: IProposalAddition<IDraftItem>[];
  /** Categories the proposals would add. */
  addedCategories: IProposalAddition<IDraftCategory>[];
}

export const EMPTY_PROPOSAL_PREVIEW: Readonly<IProposalPreview> = Object.freeze(
  { decorations: {}, changed: {}, added: [], addedCategories: [] }
);

/** What the pending proposals of the latest done turn would do to the list. */
export function proposalPreview(turns: IMenuImportTurn[]): IProposalPreview {
  const proposals = latestPendingProposals(turns);
  if (proposals.length === 0) return EMPTY_PROPOSAL_PREVIEW;
  const decorations = rowDecorations(turns);
  const preview: IProposalPreview = {
    decorations,
    changed: {},
    added: [],
    addedCategories: [],
  };
  proposals.forEach((proposal, index) => {
    for (const effect of proposal.effects) {
      const key = `p${index}:${effect.id}`;
      if (effect.entity === 'item' && isItemRecord(effect.after)) {
        if (effect.change === 'add')
          preview.added.push({ key, proposal: index, record: effect.after });
        else if (
          effect.change === 'update' &&
          decorations[effect.id] === 'changed'
        )
          preview.changed[effect.id] = effect.after;
      } else if (
        effect.entity === 'category' &&
        effect.change === 'add' &&
        isCategoryRecord(effect.after)
      ) {
        preview.addedCategories.push({
          key,
          proposal: index,
          record: effect.after,
        });
      }
    }
  });
  return preview;
}

export interface IDraftListRow {
  key: string;
  /** What the row shows: the draft's dish, or the proposed one when marked. */
  item: IDraftItem;
  /** The dish as it is now, when the row shows a proposed change of it. */
  before: IDraftItem | null;
  decoration: TRowDecoration | null;
  /** A dish a proposal would add: not in the draft yet, nothing to edit. */
  ghost: boolean;
}

export interface IDraftListSection {
  key: string;
  category: IDraftCategory;
  /** A category a proposal would add. */
  ghost: boolean;
  rows: IDraftListRow[];
  /** Every dish of the category, whatever the filter («{count} platos»). */
  total: number;
  /** Its amber and red dishes. */
  toCheck: number;
  /** Update sessions: its dishes whose price changes. */
  priceChanged: number;
  /** Update sessions: its dishes already on the menu with nothing to change. */
  unchanged: number;
  /**
   * Starts collapsed («Ver {count} platos»): a big category with nothing to
   * review, or (update sessions) one whose every dish is unchanged and
   * none needs a look.
   */
  defaultCollapsed: boolean;
}

function draftRow(item: IDraftItem, preview: IProposalPreview): IDraftListRow {
  const decoration = preview.decorations[item.id] ?? null;
  const proposed =
    decoration === 'changed' ? preview.changed[item.id] : undefined;
  return {
    key: item.id,
    item: proposed ?? item,
    before: proposed ? item : null,
    decoration,
    ghost: false,
  };
}

function ghostRow(addition: IProposalAddition<IDraftItem>): IDraftListRow {
  return {
    key: `ghost:${addition.key}`,
    item: addition.record,
    before: null,
    decoration: 'new',
    ghost: true,
  };
}

/**
 * The sections of the list. «Todos» keeps empty categories (building by
 * hand adds dishes to them); the other filters keep only categories with a
 * matching row. Proposed dishes follow the dishes of their category;
 * proposed categories come last.
 *
 * `keepItemIds` (the open dish) stay listed whatever the filter: fixing a
 * dish in its editor must not make the editor vanish under the owner's
 * fingers. It leaves a filter once it is closed.
 */
export function draftListSections(
  draft: IMenuDraft,
  filter: TItemFilter,
  preview: IProposalPreview = EMPTY_PROPOSAL_PREVIEW,
  keepItemIds: readonly string[] = []
): IDraftListSection[] {
  const sections: IDraftListSection[] = [];
  const keep = (rows: IDraftListRow[]) => filter === 'all' || rows.length > 0;
  const shown = (item: IDraftItem) =>
    keepItemIds.includes(item.id) || matchesItemFilter(item, filter);

  const update = isUpdateDraft(draft);
  for (const category of orderedCategories(draft)) {
    const items = categoryItems(draft, category);
    const toCheck = items.filter((item) => item.review.level !== 'ok').length;
    const states = update ? items.map(liveItemState) : [];
    const priceChanged = states.filter((s) => s === 'priceChanged').length;
    const unchanged = states.filter((s) => s === 'unchanged').length;
    const rows = [
      ...items.filter(shown).map((item) => draftRow(item, preview)),
      ...preview.added
        .filter(
          (addition) =>
            addition.record.categoryId === category.id &&
            matchesItemFilter(addition.record, filter)
        )
        .map(ghostRow),
    ];
    if (!keep(rows)) continue;
    sections.push({
      key: category.id,
      category,
      ghost: false,
      rows,
      total: items.length,
      toCheck,
      priceChanged,
      unchanged,
      defaultCollapsed:
        (items.length > COLLAPSE_MIN_ITEMS && toCheck === 0) ||
        (update &&
          items.length > 0 &&
          unchanged === items.length &&
          toCheck === 0),
    });
  }

  for (const addition of preview.addedCategories) {
    if (draft.categories[addition.record.id]) continue;
    const rows = preview.added
      .filter(
        (item) =>
          item.proposal === addition.proposal &&
          item.record.categoryId === addition.record.id &&
          matchesItemFilter(item.record, filter)
      )
      .map(ghostRow);
    if (!keep(rows)) continue;
    sections.push({
      key: `ghost:${addition.key}`,
      category: addition.record,
      ghost: true,
      rows,
      total: rows.length,
      toCheck: 0,
      priceChanged: 0,
      unchanged: 0,
      defaultCollapsed: false,
    });
  }

  return sections;
}

/**
 * A section shows collapsed («Ver {count} platos»): only under «Todos» (a
 * filter already narrows the list), a big category with nothing to review
 * that was never shown open (`openKeys`) and holds neither the open nor the
 * selected dish (`holdItemIds`).
 */
export function isSectionCollapsed(
  section: IDraftListSection,
  filter: TItemFilter,
  openKeys: ReadonlySet<string>,
  holdItemIds: readonly string[]
): boolean {
  return (
    filter === 'all' &&
    section.defaultCollapsed &&
    !openKeys.has(section.key) &&
    !section.rows.some((row) => !row.ghost && holdItemIds.includes(row.item.id))
  );
}

/**
 * Sections shown open under «Todos» that `openKeys` does not hold yet. Once
 * shown open, a category stays open: fixing its last flagged dish, or a
 * read adding dishes, never folds it under the owner. Collapsing is decided
 * when a category is first shown.
 */
export function newlyOpenSectionKeys(
  sections: IDraftListSection[],
  filter: TItemFilter,
  openKeys: ReadonlySet<string>,
  holdItemIds: readonly string[]
): string[] {
  if (filter !== 'all') return [];
  return sections
    .filter(
      (section) =>
        !section.ghost &&
        !openKeys.has(section.key) &&
        !isSectionCollapsed(section, filter, openKeys, holdItemIds)
    )
    .map((section) => section.key);
}

/** The category holding a dish (for expanding it before showing the dish). */
export function sectionOfItem(
  sections: IDraftListSection[],
  itemId: string
): IDraftListSection | null {
  return (
    sections.find((section) =>
      section.rows.some((row) => !row.ghost && row.item.id === itemId)
    ) ?? null
  );
}
